import { SPREADSHEET_FILE_EXTENSIONS } from '@shared/constants/FileExtensions';

// Mime types Filestack/browsers report for spreadsheets that we can convert to PDF.
const SPREADSHEET_MIME_TYPES = [
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
  'application/vnd.ms-excel', // .xls
  'text/csv', // .csv
];

/**
 * Build a Filestack PDF-conversion URL for a spreadsheet document by inserting an `output=format:pdf/` task before the file handle.
 * Only for `cdn.filestackcontent.com` URLs; anything else returns null and the caller falls back to the original URL.
 *   https://cdn.filestackcontent.com/<handle>  ->  https://cdn.filestackcontent.com/output=format:pdf/<handle>
 */
export function filestackPdfConversionUrl(url: string): string | null {
  const FILESTACK_CDN = 'https://cdn.filestackcontent.com/';
  if (!url.startsWith(FILESTACK_CDN)) return null;
  // Already a conversion URL — leave it alone.
  if (url.slice(FILESTACK_CDN.length).startsWith('output=')) return url;
  return `${FILESTACK_CDN}output=format:pdf/${url.slice(FILESTACK_CDN.length)}`;
}

/** True if a filename/title/url ends with a known spreadsheet extension. */
export function isSpreadsheetName(name: string | undefined | null): boolean {
  if (!name) return false;
  const lower = name.toLowerCase();
  return SPREADSHEET_FILE_EXTENSIONS.some((ext) => lower.endsWith(`.${ext}`));
}

/** True if a blob's mime type is a spreadsheet type we can convert to PDF. */
export function isSpreadsheetMimeType(mimeType: string | undefined | null): boolean {
  if (!mimeType) return false;
  return SPREADSHEET_MIME_TYPES.includes(mimeType);
}

/**
 * `onDone` fires once the print dialog has closed and the iframe is gone — the only safe point
 * at which a caller may revoke an object URL it created, since the iframe needs it until then.
 */
export function printUrl(url: string, onDone?: () => void) {
  const iframe = document.createElement('iframe');
  iframe.style.display = 'none';
  iframe.src = url;
  document.body.appendChild(iframe);

  iframe.onload = () => {
    iframe.contentWindow?.print();
    window.addEventListener(
      'focus',
      () => {
        iframe.remove();
        onDone?.();
      },
      { once: true }
    );
  };
}
